import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ClipboardList, ChevronDown, Globe, Phone, Target, UserRound } from 'lucide-react';

const MARKET_LABELS = { US: 'United States', UK: 'United Kingdom', EU: 'Europe', India: 'India' };

export const MarketBadge = ({ market, testId }) => market ? (
    <Badge variant="outline" className="gap-1 text-[11px] font-medium" data-testid={testId}>
        <Globe className="h-3 w-3" /> {MARKET_LABELS[market] || market}
    </Badge>
) : null;

const FactList = ({ facts, testId }) => (
    <ol className="space-y-2" data-testid={testId}>
        {facts.map((fact, i) => (
            <li key={fact} className="flex gap-3 text-sm leading-relaxed">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">{i + 1}</span>
                <span>{fact}</span>
            </li>
        ))}
    </ol>
);

const callTitle = (simulation, scenario) => simulation?.variant_title || simulation?.topic_data?.name || scenario?.title;

export const PreCallBrief = ({ simulation, scenario, onStart }) => {
    const ctx = simulation.scenario_context;
    const Icon = scenario?.icon || ClipboardList;
    return (
        <Card className={`mx-auto max-w-3xl overflow-hidden border-2 ${scenario?.borderColor || 'border-primary/30'} animate-fade-in`} data-testid="pre-call-brief">
            <div className={`px-6 py-5 sm:px-8 ${scenario?.bgColor || 'bg-primary/10'}`}>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    <ClipboardList className="h-4 w-4" /> Your call brief
                </p>
                <div className="mt-3 flex items-start gap-3">
                    <div className="rounded-lg bg-background/60 p-2"><Icon className={`h-5 w-5 ${scenario?.color || 'text-primary'}`} /></div>
                    <div className="min-w-0">
                        <h2 className="text-xl font-semibold leading-tight sm:text-2xl" data-testid="pre-call-brief-title">{callTitle(simulation, scenario)}</h2>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                            {ctx.persona && <span className="flex items-center gap-1" data-testid="pre-call-brief-persona"><UserRound className="h-3.5 w-3.5" />{ctx.persona}</span>}
                            <MarketBadge market={ctx.market} testId="pre-call-brief-market" />
                        </div>
                    </div>
                </div>
            </div>
            <CardContent className="space-y-6 p-6 sm:p-8">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="pre-call-brief-goal">
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary"><Target className="h-4 w-4" /> Your goal</p>
                    <p className="mt-1.5 text-base font-medium">{ctx.brief.goal}</p>
                </div>
                <div>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Facts you know going in</p>
                    <FactList facts={ctx.brief.facts} testId="pre-call-brief-facts" />
                </div>
                <p className="text-xs text-muted-foreground">
                    The other person knows things you don't. Ask good questions to uncover them. This brief stays open beside the call.
                </p>
                <Button onClick={onStart} size="lg" className="w-full gap-2 sm:w-auto" data-testid="start-call-btn">
                    <Phone className="h-4 w-4" /> I'm ready, start the call
                </Button>
            </CardContent>
        </Card>
    );
};

export const CallBriefPanel = ({ simulation }) => {
    const [open, setOpen] = useState(true);
    const brief = simulation?.scenario_context?.brief;
    if (!brief?.facts?.length) return null;
    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <Card className="border-primary/20" data-testid="call-brief-panel">
                <CollapsibleTrigger asChild>
                    <button type="button" className="w-full text-left" data-testid="call-brief-toggle">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="flex items-center gap-2 text-sm font-medium">
                                <ClipboardList className="h-4 w-4 text-primary" /> Call Brief
                            </CardTitle>
                            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                        </CardHeader>
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="space-y-3 pt-0">
                        <MarketBadge market={simulation.scenario_context.market} testId="call-brief-market" />
                        <p className="text-xs font-medium"><span className="text-primary">Goal:</span> {brief.goal}</p>
                        <FactList facts={brief.facts} testId="call-brief-facts" />
                    </CardContent>
                </CollapsibleContent>
            </Card>
        </Collapsible>
    );
};
